import { FC, useEffect, useRef } from "react";

import { useFlags } from "launchdarkly-react-client-sdk";

import { captureException } from "src/lib/monitoring";
import { useLocation } from "src/router";
import { load } from "src/utils/storage";

import {
  RELOAD_ENABLED,
  persistAppVersion,
  recordAppVersion,
  shouldForceReload,
} from "./force-reload-utils";

const APP_VERSION_KEY = "app-version";

const preloadedAppVersion = load(APP_VERSION_KEY);

export const ForceReload: FC = () => {
  const { appVersion } = useFlags();

  const location = useLocation();

  const hasReconciledRef = useRef(false);
  const prevLocationKeyRef = useRef(location.key);
  const reportedSkipRef = useRef(false);

  useEffect(() => {
    // Track boot vs. latest version so the error boundary can reload a stale
    // bundle that crashed, even though this component lives inside the subtree
    // the boundary unmounts on crash.
    recordAppVersion(appVersion);

    const navigated = location.key !== prevLocationKeyRef.current;
    prevLocationKeyRef.current = location.key;

    const reload =
      RELOAD_ENABLED &&
      shouldForceReload({
        appVersion,
        storedAppVersion: preloadedAppVersion,
        navigated,
        hasReconciled: hasReconciledRef.current,
      });

    if (appVersion) {
      hasReconciledRef.current = true;
    }

    if (!reload) {
      return;
    }
    if (persistAppVersion(APP_VERSION_KEY, appVersion)) {
      window.location.reload();
    } else if (!reportedSkipRef.current) {
      reportedSkipRef.current = true;
      captureException(
        new Error("Skipped app version reload: could not save the version"),
        { tags: { component: "ForceReload" } },
      );
    }
  }, [appVersion, location]);

  return null;
};
